import { type SyntheticEvent, useEffect, useState } from 'react';

import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';

import { passwordCopy } from '@nesso/ui/lib/password.schema';

import { confirmPasswordReset, getPasswordResetTokenStatus } from '../api/confirm-password-reset';
import { confirmPasswordResetSchema } from '../model/confirm-password-reset.schema';

const genericErrorCopy = 'Something went wrong. Please try again.';

type TokenState = 'checking' | 'valid' | 'invalid';

type FieldErrors = {
    password?: string;
    form?: string;
};

export function useConfirmPasswordReset(token: string) {
    const navigate = useNavigate();
    const [tokenState, setTokenState] = useState<TokenState>('checking');
    const [password, setPassword] = useState('');
    const [errors, setErrors] = useState<FieldErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        let active = true;
        getPasswordResetTokenStatus(token).then((status) => {
            if (active) setTokenState(status.valid ? 'valid' : 'invalid');
        });
        return () => {
            active = false;
        };
    }, [token]);

    const handlePasswordBlur = () => {
        const result = confirmPasswordResetSchema.shape.password.safeParse(password);
        setErrors((prev) => ({
            ...prev,
            password: result.success ? undefined : result.error.issues[0]?.message,
        }));
    };

    const handleSubmit = async (
        event: SyntheticEvent<HTMLFormElement>,
    ): Promise<'password' | undefined> => {
        event.preventDefault();
        if (isSubmitting) return;

        const parsed = confirmPasswordResetSchema.safeParse({ password });
        if (!parsed.success) {
            setErrors({ password: parsed.error.issues[0]?.message });
            return 'password';
        }

        setErrors({});
        setIsSubmitting(true);

        const result = await confirmPasswordReset({ token, password: parsed.data.password });

        if (result.ok) {
            toast.success('Your password has been reset.');
            void navigate({ to: '/login', search: {}, replace: true });
            return;
        }

        setIsSubmitting(false);
        switch (result.code) {
            case 'WEAK_PASSWORD':
                setErrors({ password: passwordCopy.complexity });
                return 'password';
            case 'LINK_INVALID':
                setTokenState('invalid');
                return undefined;
            default:
                setErrors({ form: genericErrorCopy });
                return undefined;
        }
    };

    return {
        tokenState,
        password,
        errors,
        isSubmitting,
        setPassword: (value: string) => {
            setPassword(value);
            if (errors.password || errors.form) {
                setErrors((prev) => ({ ...prev, password: undefined, form: undefined }));
            }
        },
        handlePasswordBlur,
        handleSubmit,
    };
}
